var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { DunningStage } from '../identity/types.js';
/**
 * One eligible invoice in the DunningRun candidates list.
 *
 * Rendered as a row in `DunningRunCandidatesView`. `lastDunnedAt` powers the
 * "letzte Mahnung am DD.MM." subline below the patient name; `null` when the
 * invoice has never been dunned. `nextDunningStage` is the escalation step the
 * DunningRun would issue.
 */
export class DunningRunCandidateRowDto {
    invoiceId;
    invoiceNumber;
    officeLocationId;
    patientId;
    patientFirstName;
    patientLastName;
    /** ISO date string (YYYY-MM-DD). */
    dueDate;
    daysOverdue;
    nextDunningStage;
    /** Mahngebühr in cents for the next stage, derived from `DunningPolicy`. */
    dunningFeeCents;
    /** Offener Betrag in cents (total minus Zahlungen). Powers the right-aligned amount column. */
    openAmountCents;
    /** ISO 4217 currency of the invoice (e.g. "EUR"). */
    currency;
    /** ISO date string of the most recent prior DunningLetter, or null. */
    lastDunnedAt;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateRowDto.prototype, "invoiceId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateRowDto.prototype, "invoiceNumber", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateRowDto.prototype, "officeLocationId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateRowDto.prototype, "patientId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], DunningRunCandidateRowDto.prototype, "patientFirstName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], DunningRunCandidateRowDto.prototype, "patientLastName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateRowDto.prototype, "dueDate", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidateRowDto.prototype, "daysOverdue", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateRowDto.prototype, "nextDunningStage", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidateRowDto.prototype, "dunningFeeCents", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidateRowDto.prototype, "openAmountCents", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateRowDto.prototype, "currency", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], DunningRunCandidateRowDto.prototype, "lastDunnedAt", void 0);
/**
 * Per-currency subtotal within a candidate group. Sums are kept per currency so
 * a group spanning OfficeLocations with different currencies never collapses
 * into a single silently-wrong total. In the common single-currency case this
 * array has exactly one entry.
 */
export class DunningRunCandidateGroupSumDto {
    currency;
    /** Sum of Offene Beträge (receivables) for this currency, in cents. */
    receivableCents;
    /** Sum of Mahngebühren for this currency, in cents. */
    dunningFeeCents;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateGroupSumDto.prototype, "currency", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidateGroupSumDto.prototype, "receivableCents", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidateGroupSumDto.prototype, "dunningFeeCents", void 0);
/**
 * One group of candidate rows by next DunningStage. The header carries the row
 * count, the per-stage Mahngebühr subtotal, and per-currency receivable +
 * Mahngebühr sums for the group-header amounts.
 */
export class DunningRunCandidateGroupDto {
    dunningStage;
    count;
    totalDunningFeeCents;
    /** Per-currency receivable + Mahngebühr subtotals for the group header. */
    sums;
    rows;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], DunningRunCandidateGroupDto.prototype, "dunningStage", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidateGroupDto.prototype, "count", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidateGroupDto.prototype, "totalDunningFeeCents", void 0);
__decorate([
    Expose(),
    Type(() => DunningRunCandidateGroupSumDto),
    __metadata("design:type", Array)
], DunningRunCandidateGroupDto.prototype, "sums", void 0);
__decorate([
    Expose(),
    Type(() => DunningRunCandidateRowDto),
    __metadata("design:type", Array)
], DunningRunCandidateGroupDto.prototype, "rows", void 0);
/**
 * Response of `DunningRunCandidateQuery`. Groups appear in escalation order
 * (Zahlungserinnerung → Erste Mahnung → Zweite Mahnung). Empty groups are
 * omitted; an empty `groups` array means there are no candidates and the
 * frontend renders the empty state.
 */
export class DunningRunCandidatesResponseDto {
    groups;
    /**
     * Count of overdue invoices excluded from this run purely by an active
     * Mahnsperre — the reviewer's confirmation that their blocks took effect.
     * Rendered as "N Rechnungen durch Mahnsperre ausgeschlossen" when > 0.
     */
    excludedByBlockCount;
}
__decorate([
    Expose(),
    Type(() => DunningRunCandidateGroupDto),
    __metadata("design:type", Array)
], DunningRunCandidatesResponseDto.prototype, "groups", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], DunningRunCandidatesResponseDto.prototype, "excludedByBlockCount", void 0);
//# sourceMappingURL=dunning-run-candidate.response.dto.js.map